import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';

import { environment } from '@dps/env';
import {
  OLDEST_PENDING_CONTRACT_CONFIRMATION_LOOKBACK,
  PENDING_CONTRACT_CONFIRMATION_STATUSES,
} from '@dps/shared/constants';
import {
  ContractConfirmation,
  ContractConfirmationDaySchedule,
  PageableResponsePayloadModel,
} from '@dps/shared/models';
import { BaseApi } from '../models/base-api';
import { ContractsConfirmationsListRequestParams } from './contracts-confirmations-list-request-params';

@Injectable({ providedIn: 'root' })
export class ContractConfirmationApiService extends BaseApi {
  private http = inject(HttpClient);

  private readonly CONTRACTS_CONFIRMATIONS_API_URL = `${environment.apiBaseUrl}/companies`;

  getContractsConfirmations(params: ContractsConfirmationsListRequestParams) {
    return this.http.get<PageableResponsePayloadModel<ContractConfirmation>>(
      `${this.CONTRACTS_CONFIRMATIONS_API_URL}/${params.companyId}/actuals`,
      {
        params: this.mapParamsToString(params),
      }
    );
  }

  /**
   * Returns the oldest contract confirmation that still has days waiting to be confirmed, looking
   * back at most {@link OLDEST_PENDING_CONTRACT_CONFIRMATION_LOOKBACK}, or `null` when there is none.
   */
  getOldestPendingContractConfirmation(companyId: string): Observable<ContractConfirmation | null> {
    const today = DateTime.now();

    return this.getContractsConfirmations({
      companyId,
      startDate: today
        .minus(OLDEST_PENDING_CONTRACT_CONFIRMATION_LOOKBACK)
        .startOf('week')
        .toISODate() as string,
      endDate: today.endOf('week').toISODate() as string,
      statuses: PENDING_CONTRACT_CONFIRMATION_STATUSES,
      size: 1,
      sort: 'dateFrom,asc',
    }).pipe(map(({ content }) => content[0] ?? null));
  }

  getContractsConfirmationsCount(companyId: string): Observable<number> {
    return this.http
      .get<{
        notificationCount: number;
      }>(`${this.CONTRACTS_CONFIRMATIONS_API_URL}/${companyId}/actuals/notificationCount`)
      .pipe(map(resp => resp.notificationCount));
  }

  updateContractConfirmationWorkTime(
    companyId: string,
    contractConfirmationId: string,
    workTime: Array<ContractConfirmationDaySchedule>
  ): Observable<Array<ContractConfirmationDaySchedule>> {
    return this.http.patch<Array<ContractConfirmationDaySchedule>>(
      `${this.CONTRACTS_CONFIRMATIONS_API_URL}/${companyId}/actuals/${contractConfirmationId}/workTimes`,
      workTime
    );
  }
}
